import React, { useEffect, useState } from 'react';
import { Clock, ChevronRight, AlertCircle, CheckCircle2, Package, Truck, Globe, Smartphone, User, XCircle, Search, Hash, Phone, Calendar, ShieldCheck } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export type OrderStatus = 'Pending' | 'Accepted' | 'Picking' | 'Searching for Driver' | 'Awaiting Partner Courier' | 'Driver Assigned' | 'Out for Delivery' | 'Delivered' | 'Delivery Attempt Failed' | 'Held' | 'Cancelled' | 'Rejected' | 'Reassigned' | 'Awaiting Payment' | 'Ready for Pickup' | 'Return Requested' | 'Waiting Return Confirmation' | 'Refunded' | 'Returned to Stock';
export type Channel = 'Website' | 'Aggregator' | 'Yaser eRx' | 'Walk-in' | 'Insurance Platform';
export type ChannelPartner = 'Jahez' | 'HungerStation' | 'Yaser' | 'Waseel' | 'Nafees';
export type PayerType = 'Cash' | 'Insurance' | 'Partner Prepaid' | 'Deferred';
export type PaymentType = 'COD' | 'Visa' | 'Mada' | 'ApplePay' | 'Tamara' | 'Tabby' | 'Prepaid';
export type LineStatus = 'Pending' | 'Picked' | 'Partially Picked' | 'Unavailable';
export interface LotNumber {
  number: string;
  expiryDate: string;
  stock: number;
}
export interface OrderLine {
  itemId: string;
  id: string;
  barcode: string;
  tradeDrugCode: string;
  sku: string;
  genericCode: string;
  qty: number;
  quantity: number;
  uom: string;
  unitPrice: number;
  requiresLot: boolean;
  allowSubstitution: boolean;
  instructionsAr?: string;
  pickedQuantity: number;
  dispensedTradeDrugCode?: string;
  selectedLot?: string;
  selectedLotNumber?: string;
  status: LineStatus;
  name: string;
  image: string;
  lotNumbers?: LotNumber[];
  isSerialized: boolean;
  serialNumbers: string[];
  requiresPrescription?: boolean;
}
export interface Customer {
  name: string;
  mobile: string;
  nationalId?: string;
  gender?: 'Male' | 'Female';
  dateOfBirth?: string;
}
export interface Address {
  line1: string;
  city: string;
  district: string;
  zipCode?: string;
  nationalShortAddress?: string;
}
export interface InsuranceDetails {
  payerName: string;
  policyNo: string;
  memberId: string;
  approvalNo?: string;
  copayPercent: number;
  patientShare: number;
  insuranceShare: number;
  paymentLinkStatus: 'Pending' | 'Paid' | 'Expired';
}
export interface PrescriptionActivity {
  genericCode: string;
  instructionsAr: string;
  durationDays: number;
}
export interface PrescriptionDetails {
  erxReference: string;
  activities: PrescriptionActivity[];
  acceptableTradeDrugs: Record<string, string[]>;
  verified?: boolean;
  image?: string;
}
export interface DriverInfo {
  name: string;
  phone: string;
  vehiclePlate: string;
  rating: number;
  avatar?: string;
}
export interface HistoryEvent {
  id: string;
  timestamp: string;
  status: OrderStatus;
  message: string;
  actor: 'Pharmacist' | 'System' | 'Driver' | 'Customer Service';
  actorName: string;
}
export interface BranchRouting {
  branchName: string;
  timestamp: string;
  reason?: string;
}
export interface FulfillmentProfile {
  kind: 'Counter Pickup' | 'Standard Delivery' | 'Partner Courier' | 'Insurance Settlement' | 'Yaser eRx Pickup';
  requiresPickupOtp: boolean;
  requiresNationalIdVerification: boolean;
  usesPartnerCourier: boolean;
  requiresPaymentLink: boolean;
  requiresPatientShareCollection: boolean;
  requiresPrescriptionReview: boolean;
  allowsPartialFulfillment: boolean;
  requiresExplicitPartialConfirmation: boolean;
  nextReadyStatus: OrderStatus;
}
export interface Order {
  id: string;
  customerName: string;
  customerPhone: string;
  customer: Customer;
  address: Address;
  fulfillmentMethod?: 'Pickup' | 'Delivery';
  lines: OrderLine[];
  itemsCount: number;
  totalAmount: number;
  status: OrderStatus;
  channel: Channel;
  channelPartner?: ChannelPartner;
  payerType: PayerType;
  paymentType: PaymentType;
  createdAt: string;
  slaMinutes: number;
  acceptanceDeadline: string;
  priority: 'High' | 'Medium' | 'Low';
  insurance?: InsuranceDetails;
  prescription?: PrescriptionDetails;
  rejectionReason?: string;
  holdReason?: string;
  returnReason?: string;
  driverInfo?: DriverInfo;
  courierName?: string;
  courierPhone?: string;
  discountAmount?: number;
  couponCode?: string;
  isLoyaltyCustomer?: boolean;
  loyaltyPointsUsed?: number;
  qitafAmount?: number;
  walletAmount?: number;
  otp?: string;
  otpVerified?: boolean;
  pickupVerification?: 'otp' | 'nationalId';
  sourceOrderId?: string;
  previousStatus?: OrderStatus;
  routingHistory?: BranchRouting[];
  currentBranch?: string;
  hasRetriedDelivery?: boolean;
  history: HistoryEvent[];
}
export function deriveFulfillmentProfile(order: Pick<Order, 'channel' | 'payerType' | 'pickupVerification' | 'fulfillmentMethod'>): FulfillmentProfile {
  if (order.channel === 'Walk-in') {
    return {
      kind: 'Counter Pickup',
      requiresPickupOtp: true,
      requiresNationalIdVerification: false,
      usesPartnerCourier: false,
      requiresPaymentLink: false,
      requiresPatientShareCollection: order.payerType === 'Insurance',
      requiresPrescriptionReview: false,
      allowsPartialFulfillment: true,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Ready for Pickup'
    };
  }
  if (order.channel === 'Yaser eRx') {
    return {
      kind: 'Yaser eRx Pickup',
      requiresPickupOtp: false,
      requiresNationalIdVerification: true,
      usesPartnerCourier: false,
      requiresPaymentLink: false,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: true,
      allowsPartialFulfillment: false,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Ready for Pickup'
    };
  }
  if (order.payerType === 'Insurance' || order.channel === 'Insurance Platform') {
    return {
      kind: 'Insurance Settlement',
      requiresPickupOtp: order.fulfillmentMethod === 'Pickup',
      requiresNationalIdVerification: false,
      usesPartnerCourier: false,
      requiresPaymentLink: true,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: false,
      allowsPartialFulfillment: true,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: order.fulfillmentMethod === 'Pickup' ? 'Ready for Pickup' : 'Searching for Driver'
    };
  }
  if (order.channel === 'Aggregator' || order.payerType === 'Partner Prepaid') {
    return {
      kind: 'Partner Courier',
      requiresPickupOtp: false,
      requiresNationalIdVerification: false,
      usesPartnerCourier: true,
      requiresPaymentLink: false,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: false,
      allowsPartialFulfillment: false,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Awaiting Partner Courier'
    };
  }
  return {
    kind: 'Standard Delivery',
    requiresPickupOtp: order.fulfillmentMethod === 'Pickup',
    requiresNationalIdVerification: false,
    usesPartnerCourier: false,
    requiresPaymentLink: order.payerType === 'Deferred',
    requiresPatientShareCollection: false,
    requiresPrescriptionReview: false,
    allowsPartialFulfillment: true,
    requiresExplicitPartialConfirmation: order.channel === 'Website',
    nextReadyStatus: order.fulfillmentMethod === 'Pickup' ? 'Ready for Pickup' : 'Searching for Driver'
  };
}
interface OrderQueueProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
const channels: Channel[] = ['Website', 'Aggregator', 'Yaser eRx', 'Walk-in', 'Insurance Platform'];
export function OrderQueue({
  orders,
  onSelectOrder
}: OrderQueueProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState('');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const filteredOrders = orders.filter(order => {
    const belongsInHistory = ['Delivered', 'Cancelled', 'Rejected', 'Reassigned', 'Return Requested', 'Refunded', 'Returned to Stock'].includes(order.status);
    if (belongsInHistory) return false;
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customer.mobile.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    const matchesDate = !dateFilter || order.createdAt.includes(dateFilter);
    return matchesSearch && matchesPhone && matchesSource && matchesDate;
  });
  return <div className="space-y-6" data-id="element-692" data-mp="qy0izudt1jmx">
      <div className="flex items-center justify-between" data-id="element-693" data-mp="qzbnxxizkxfm">
        <div data-id="element-694" data-mp="lzpapo35rh6t">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-695" data-mp="m07ed5s5gxor">Order Queue</h2>
          <p className="text-slate-500" data-id="element-696" data-mp="zz73eejkrelg">Manage incoming orders and fulfillment SLAs</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-697" data-mp="uzm3o1wkz27o">
        <div className="space-y-2" data-id="element-698" data-mp="19wv1nege348d">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-699" data-mp="19vtnh142u1mf"><Hash className="h-3 w-3" data-id="element-700" data-mp="14xs2gan3bs8b" />Order ID / Name</label>
          <div className="relative" data-id="element-701" data-mp="1iq82d2kp3a50">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-702" data-mp="1ip6kof96bpeb" />
            <input type="text" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Search ID or Name..." className="w-full rounded-xl border-2 border-transparent bg-slate-50 py-2 pl-10 pr-4 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-703" data-mp="1eomr1lnt73wx" />
          </div>
        </div>
        <div className="space-y-2" data-id="element-704" data-mp="2xjtx7m2flbkr">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-705" data-mp="2xir89joo5j7h"><Phone className="h-3 w-3" data-id="element-706" data-mp="2tja20o1f4i19" />Customer Mobile</label>
          <div className="relative" data-id="element-707" data-mp="2oj46uxvxwdef">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-708" data-mp="2ok6t2sibhro0" />
            <input type="text" value={phoneQuery} onChange={event => setPhoneQuery(event.target.value)} placeholder="9665XXXXXXXX" className="w-full rounded-xl border-2 border-transparent bg-slate-50 py-2 pl-10 pr-4 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-709" data-mp="3l8d540hkjmcb" />
          </div>
        </div>
        <div className="space-y-2" data-id="element-710" data-mp="3793stbwhhcdm">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-711" data-mp="20tjlk8cbd41z"><Globe className="h-3 w-3" data-id="element-712" data-mp="1vugfckklkrsf" />Source</label>
          <select value={sourceFilter} onChange={event => setSourceFilter(event.target.value)} className="w-full appearance-none rounded-xl border-2 border-transparent bg-slate-50 px-4 py-2 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-713" data-mp="1vsvjmrmjr2sz">
            <option value="all" data-id="element-714" data-mp="1vofqlu65ajy4">All Sources</option>
            {channels.map(channel => <option key={channel} value={channel} data-id="element-715" data-mp="20f5xjbu7yuux">{channel}</option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-716" data-mp="24h9kawmc9lvi">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-717" data-mp="24g7wrso4z6ww"><Calendar className="h-3 w-3" data-id="element-718" data-mp="29esxrub7pume" />Date</label>
          <input type="date" value={dateFilter} onChange={event => setDateFilter(event.target.value)} className="w-full rounded-xl border-2 border-transparent bg-slate-50 px-4 py-2 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-719" data-mp="2ef76lekn18lh" />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-720" data-mp="k3ir9bahv40k">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-721" data-mp="p23qp8hx9jny">
          <div data-id="element-722" data-mp="c2krh22o4sps">Order ID</div><div data-id="element-723" data-mp="qxr262cplfpg">Customer</div><div data-id="element-724" data-mp="qyaaqsggyrqt">Source</div><div data-id="element-725" data-mp="qyscg4lwhy64">Status</div><div data-id="element-726" data-mp="qzbwprz0wynb">Fulfillment</div><div data-id="element-727" data-mp="qvo0um63w05f">Acceptance SLA</div><div data-id="element-728" data-mp="lynwcfown5gf">Amount / Payer</div><div className="w-10" data-id="element-729" data-mp="lyefbhlu88mv" />
        </div>
        <div className="divide-y divide-slate-100" data-id="element-730" data-mp="m0qvbp81c8xa">
          {filteredOrders.map(order => {
          const profile = deriveFulfillmentProfile(order);
          return <button key={order.id} onClick={() => onSelectOrder(order)} className={cn('group grid w-full grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] items-center gap-4 px-6 py-5 text-left transition-colors', order.slaMinutes < 10 ? 'bg-rose-50/50 hover:bg-rose-50' : 'hover:bg-slate-50')} data-id="element-731" data-mp="uyt2kmid3xt0">
                <div className="font-mono font-bold text-blue-600" data-id="element-732" data-mp="1ipgctefr2ziq">#{order.id}</div>
                <div data-id="element-733" data-mp="1eocwyrxpd3hi"><p className="font-semibold text-slate-900" data-id="element-734" data-mp="1eomowl2b5kpu">{order.customer.name}</p><p className="text-xs text-slate-500" data-id="element-735" data-mp="1xb3yx55ike48">{order.lines.length} items · {order.customer.mobile}</p></div>
                <div className="space-y-1" data-id="element-736" data-mp="22ayqzc10z9qv"><SourceBadge channel={order.channel} data-id="element-737" data-mp="22a6ulyzlt7zj" /><p className="text-[10px] font-bold uppercase text-slate-400" data-id="element-738" data-mp="1oaxqhxooa4bx">{order.channelPartner || order.payerType}</p></div>
                <StatusBadge status={order.status} data-id="element-739" data-mp="1t990c8eyweu5" />
                <div className="space-y-1" data-id="element-740" data-mp="1tc4wbr798tfy"><p className="text-[10px] font-bold uppercase text-slate-600" data-id="element-741" data-mp="1ele44i1r3scb">{profile.kind}</p>{profile.usesPartnerCourier && <p className="text-[9px] font-medium text-slate-400" data-id="element-742" data-mp="1jce2zsufkkhg">System partner dispatch</p>}</div>
                <div data-id="element-743" data-mp="15kzfp9thpx4l">
                  {order.status === 'Pending' ? <AcceptanceCountdown deadline={order.acceptanceDeadline} now={now} data-id="element-744" data-mp="19m18p8wtdcy4" /> : <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase', order.slaMinutes < 10 ? 'bg-red-50 text-red-700' : order.slaMinutes < 20 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')} data-id="element-745" data-mp="vmjmn1awkdsg"><Clock className="h-3 w-3" data-id="element-746" data-mp="22y99i0d28wnn" />{order.slaMinutes}m</div>}
                </div>
                <div data-id="element-747" data-mp="1yzaasxhx9w4n"><p className="font-bold text-slate-900" data-id="element-748" data-mp="1yza82pqjfi0y">{order.totalAmount.toFixed(2)} SAR</p><PayerBadge payerType={order.payerType} data-id="element-749" data-mp="1u4wcam7yr8vr" /></div>
                <div className="flex items-center gap-3" data-id="element-750" data-mp="1p85w7yw5sdac">{order.prescription && <ShieldCheck className={cn('h-4 w-4', order.prescription.verified ? 'text-emerald-500' : 'text-purple-500')} title={order.prescription.verified ? 'eRx verified' : 'eRx review required'} data-id="element-751" data-mp="1p5jnvx1g1tr2" />}<ChevronRight className="h-5 w-5 text-slate-300 transition-colors group-hover:text-blue-600" data-id="element-752" data-mp="1fbb0tov6mt0a" /></div>
              </button>;
        })}
        </div>
      </div>
    </div>;
}
function SourceBadge({
  channel
}: {
  channel: Channel;
}) {
  const isDigital = channel === 'Website' || channel === 'Yaser eRx' || channel === 'Insurance Platform';
  return <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase', isDigital ? 'bg-indigo-50 text-indigo-700' : channel === 'Walk-in' ? 'bg-purple-50 text-purple-700' : 'bg-orange-50 text-orange-700')} data-id="element-753" data-mp="30nuj6q9excb5">{isDigital ? <Globe className="h-3 w-3" data-id="element-754" data-mp="3hw3v2cvmqoj5" /> : channel === 'Walk-in' ? <User className="h-3 w-3" data-id="element-755" data-mp="3hwdiptt7cjlo" /> : <Smartphone className="h-3 w-3" data-id="element-756" data-mp="3hy7yktpm9liw" />}{channel}</div>;
}
function PayerBadge({
  payerType
}: {
  payerType: PayerType;
}) {
  const labels: Record<PayerType, string> = {
    Cash: 'Cash',
    Insurance: 'Insurance',
    'Partner Prepaid': 'Partner',
    Deferred: 'Deferred'
  };
  const styles: Record<PayerType, string> = {
    Cash: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    Insurance: 'bg-violet-50 text-violet-700 border-violet-100',
    'Partner Prepaid': 'bg-sky-50 text-sky-700 border-sky-100',
    Deferred: 'bg-amber-50 text-amber-700 border-amber-100'
  };
  return <span className={cn('mt-1 inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wide', styles[payerType])} data-id="element-757" data-mp="eh7sfyf9zkxs">{labels[payerType]}</span>;
}
function AcceptanceCountdown({
  deadline,
  now
}: {
  deadline: string;
  now: number;
}) {
  const remainingSeconds = Math.max(0, Math.floor((new Date(deadline).getTime() - now) / 1000));
  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor(remainingSeconds % 3600 / 60);
  const seconds = remainingSeconds % 60;
  const isExpired = remainingSeconds === 0;
  const label = isExpired ? 'Acceptance overdue' : `${hours > 0 ? `${hours}h ` : ''}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tabular-nums', isExpired ? 'bg-red-50 text-red-700' : remainingSeconds < 600 ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700')} data-id="element-758" data-mp="25j6rjhcn0fyt"><Clock className="h-3 w-3" data-id="element-759" data-mp="1d1ufoh9uv0w8" />{label}</div>;
}
export function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<OrderStatus, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100',
    'Awaiting Partner Courier': 'bg-cyan-50 text-cyan-700 border-cyan-100',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    Rejected: 'bg-red-50 text-red-700 border-red-100',
    Reassigned: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
    'Awaiting Payment': 'bg-amber-50 text-amber-700 border-amber-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    'Waiting Return Confirmation': 'bg-amber-50 text-amber-700 border-amber-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100',
    'Returned to Stock': 'bg-teal-50 text-teal-700 border-teal-100'
  };
  const icons: Record<OrderStatus, React.ElementType> = {
    Pending: AlertCircle,
    Accepted: CheckCircle2,
    Picking: Package,
    'Searching for Driver': Truck,
    'Awaiting Partner Courier': Clock,
    'Driver Assigned': User,
    'Out for Delivery': Truck,
    Delivered: CheckCircle2,
    'Delivery Attempt Failed': AlertCircle,
    Held: Clock,
    Cancelled: XCircle,
    Rejected: XCircle,
    Reassigned: ChevronRight,
    'Awaiting Payment': ShieldCheck,
    'Ready for Pickup': Package,
    'Return Requested': AlertCircle,
    'Waiting Return Confirmation': Clock,
    Refunded: CheckCircle2,
    'Returned to Stock': Package
  };
  const Icon = icons[status];
  return <div className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase', styles[status])} data-id="element-760" data-mp="k0bge30a1djn"><Icon className="h-3 w-3" data-id="element-761" data-mp="f80hn3h9vxso" />{status}</div>;
}